import { Sparkles, Calendar, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { formatInCentralTime } from "@/utils/timezone";
import { useCowgirlsPromoEvent } from "@/hooks/useCowgirlsPromo";

/**
 * Inline promo card shown on the Home page for any active/upcoming
 * Wellness Cowgirls-hosted event. Visible to guests and authed users.
 *
 * Persistent: this banner stays visible as long as a Wellness Cowgirls
 * event is active. It is intentionally NOT dismissible — the one-time
 * popup behavior lives in CowgirlsPromoDialog on the Events page.
 */
export const HomeCowgirlsPromoBanner = () => {
  const navigate = useNavigate();
  const { event } = useCowgirlsPromoEvent();

  if (!event) return null;

  const handleView = () => {
    navigate(`/events/${event.id}`);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-pink-200 shadow-md">
      <div className="bg-gradient-to-br from-pink-400 via-pink-500 to-rose-500 text-white p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4" />
          <span className="text-[11px] font-semibold uppercase tracking-wider opacity-95">
            Wellness Cowgirls Hosted
          </span>
        </div>

        <h3 className="text-lg font-bold leading-snug">
          {event.title}
        </h3>

        <div className="mt-2 space-y-1 text-xs text-white/90">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{formatInCentralTime(event.date, "EEE, MMM d • h:mm a")} CT</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="line-clamp-1">{event.location}</span>
          </div>
        </div>

        <Button
          onClick={handleView}
          haptic
          size="sm"
          className="mt-3 w-full bg-white text-pink-600 hover:bg-white/90 font-semibold"
        >
          View Event
        </Button>
      </div>
    </div>
  );
};
